import React, { useState, useEffect, useRef } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import SafeIcon from '../../common/SafeIcon';
import { supabase, isSupabaseConfigured } from '../../lib/supabase';

const TABLE = 'design_categories_1739812345679';

function useDragReorder(items, onReorder) {
  const dragIndex = useRef(null);
  const dragOverIndex = useRef(null);
  const onDragStart = (i) => { dragIndex.current = i; };
  const onDragEnter = (i) => { dragOverIndex.current = i; };
  const onDragEnd = () => {
    const from = dragIndex.current;
    const to   = dragOverIndex.current;
    if (from === null || to === null || from === to) {
      dragIndex.current = null; dragOverIndex.current = null; return;
    }
    const next = [...items];
    const [moved] = next.splice(from, 1);
    next.splice(to, 0, moved);
    dragIndex.current = null; dragOverIndex.current = null;
    onReorder(next);
  };
  return { onDragStart, onDragEnter, onDragEnd };
}

function EmptyBadge() {
  return (
    <span className="px-2 py-0.5 rounded-full text-[10px] font-bold uppercase bg-amber-50 text-amber-500 border border-amber-100">
      No designs
    </span>
  );
}

function StatusBadge({ active }) {
  return (
    <span className={`px-2 py-0.5 rounded-full text-[10px] font-bold uppercase ${active ? 'bg-green-100 text-green-700' : 'bg-sage-100 text-sage-500'}`}>
      {active ? 'Active' : 'Inactive'}
    </span>
  );
}

export default function CategoryLibrary() {
  const [categories,      setCategories]      = useState([]);
  const [designCounts,    setDesignCounts]    = useState({});
  const [loading,         setLoading]         = useState(true);
  const [saving,          setSaving]          = useState(false);
  const [reordering,      setReordering]      = useState(false);
  const [error,           setError]           = useState('');
  const [showForm,        setShowForm]        = useState(false);
  const [editingCat,      setEditingCat]      = useState(null);
  const [confirmDelete,   setConfirmDelete]   = useState(null);
  const [deleting,        setDeleting]        = useState(false);
  const [expandedParents, setExpandedParents] = useState(new Set());

  const emptyForm = { id: '', label: '', active: true, next_day_only: false, parent_id: '' };
  const [form, setForm] = useState(emptyForm);
  const formTopRef = useRef();

  useEffect(() => { fetchAll(); }, []);

  async function fetchAll() {
    setLoading(true);
    if (!isSupabaseConfigured()) { setLoading(false); return; }
    const [catRes, designRes] = await Promise.all([
      supabase.from(TABLE).select('*').order('sort_order'),
      supabase.from('designs').select('category, active').eq('active', true),
    ]);
    const cats    = catRes.data   || [];
    const designs = designRes.data || [];
    const counts  = {};
    designs.forEach(d => { (d.category || []).forEach(cid => { counts[cid] = (counts[cid] || 0) + 1; }); });
    setCategories(cats);
    setDesignCounts(counts);
    const parentIds = new Set(cats.filter(c => c.parent_id).map(c => c.parent_id));
    setExpandedParents(parentIds);
    setLoading(false);
  }

  const parents     = categories.filter(c => !c.parent_id);
  const childrenOf  = (pid) => categories.filter(c => c.parent_id === pid).sort((a, b) => a.sort_order - b.sort_order);

  const parentDrag = useDragReorder(parents, async (reordered) => {
    const rebuilt = [];
    reordered.forEach((p, i) => {
      rebuilt.push({ ...p, sort_order: i * 10 });
      childrenOf(p.id).forEach((ch, j) => rebuilt.push({ ...ch, sort_order: i * 10 + j + 1 }));
    });
    setCategories(rebuilt);
    await persistSortOrder(rebuilt);
  });

  async function persistSortOrder(list) {
    if (!isSupabaseConfigured()) return;
    setReordering(true);
    try {
      await Promise.all(list.map(c => supabase.from(TABLE).update({ sort_order: c.sort_order }).eq('id', c.id)));
    } catch (e) { setError('Reorder failed: ' + e.message); }
    finally { setReordering(false); }
  }

  async function moveChild(parentId, childId, dir) {
    const siblings = childrenOf(parentId);
    const idx = siblings.findIndex(c => c.id === childId);
    const newIdx = idx + dir;
    if (newIdx < 0 || newIdx >= siblings.length) return;
    const reordered = [...siblings];
    [reordered[idx], reordered[newIdx]] = [reordered[newIdx], reordered[idx]];
    const parentSortBase = (parents.find(p => p.id === parentId)?.sort_order ?? 0);
    const updated = categories.map(c => {
      const pos = reordered.findIndex(r => r.id === c.id);
      return pos !== -1 ? { ...c, sort_order: parentSortBase + pos + 1 } : c;
    });
    setCategories(updated);
    await persistSortOrder(updated);
  }

  function openNew(parentId = '') {
    setEditingCat(null);
    setForm({ ...emptyForm, parent_id: parentId });
    setError('');
    setShowForm(true);
    setTimeout(() => formTopRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' }), 50);
  }

  function openEdit(cat) {
    setEditingCat(cat);
    setForm({ id: cat.id, label: cat.label, active: cat.active, next_day_only: cat.next_day_only || false, parent_id: cat.parent_id || '' });
    setError('');
    setShowForm(true);
    setTimeout(() => formTopRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' }), 50);
  }

  async function handleSave() {
    if (!form.label.trim() || !form.id.trim()) { setError('Label and ID are both required.'); return; }
    setSaving(true);
    try {
      if (isSupabaseConfigured()) {
        const payload = {
          id: form.id.trim(), label: form.label.trim(), active: form.active,
          next_day_only: form.next_day_only, parent_id: form.parent_id || null,
          ...(editingCat ? {} : { sort_order: categories.length * 10 }),
        };
        const { error: e } = await supabase.from(TABLE).upsert(payload);
        if (e) throw e;
        await fetchAll();
      }
      setShowForm(false); setEditingCat(null);
    } catch (e) { setError(e.message); }
    finally { setSaving(false); }
  }

  async function handleDelete(id) {
    setDeleting(true);
    try {
      if (isSupabaseConfigured()) {
        await supabase.from(TABLE).update({ parent_id: null }).eq('parent_id', id);
        const { error: e } = await supabase.from(TABLE).delete().eq('id', id);
        if (e) throw e;
        await fetchAll();
      }
    } catch (e) { setError(e.message); }
    finally { setDeleting(false); setConfirmDelete(null); }
  }

  async function toggleActive(cat) {
    if (!isSupabaseConfigured()) return;
    await supabase.from(TABLE).update({ active: !cat.active }).eq('id', cat.id);
    setCategories(prev => prev.map(c => c.id === cat.id ? { ...c, active: !c.active } : c));
  }

  function toggleExpanded(id) {
    setExpandedParents(prev => { const n = new Set(prev); n.has(id) ? n.delete(id) : n.add(id); return n; });
  }

  const totalDesignsFor = (cat) =>
    (designCounts[cat.id] || 0) + childrenOf(cat.id).reduce((s, ch) => s + (designCounts[ch.id] || 0), 0);

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex justify-between items-start">
        <div>
          <h2 className="text-2xl font-serif text-charcoal">Design Categories</h2>
          <p className="text-sage-500 text-sm mt-1">
            Drag rows to reorder. Empty categories are flagged and hidden from customers automatically.
          </p>
        </div>
        <div className="flex items-center gap-3">
          {reordering && <span className="text-xs text-sage-400 animate-pulse">Saving order…</span>}
          <button onClick={() => openNew()} className="btn-primary"><SafeIcon name="Plus" /> New Category</button>
        </div>
      </div>

      {error && (
        <div className="bg-red-50 border border-red-100 rounded-2xl px-5 py-3 text-red-600 text-sm font-medium flex justify-between">
          {error}
          <button onClick={() => setError('')} className="text-red-400 hover:text-red-600 font-bold ml-3">✕</button>
        </div>
      )}

      {/* Form */}
      <div ref={formTopRef} />
      <AnimatePresence>
        {showForm && (
          <motion.div initial={{ opacity: 0, y: -10 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0, y: -10 }}
            className="bg-white border border-sage-100 rounded-2xl p-6 shadow-soft space-y-5">
            <h3 className="text-sm font-bold text-charcoal uppercase tracking-widest">
              {editingCat ? `Editing: ${editingCat.label}` : 'New Category'}
            </h3>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div className="field-group">
                <label className="form-label">Display Name</label>
                <input type="text" className="input-field" placeholder="e.g. Birthday" value={form.label}
                  onChange={e => {
                    const label = e.target.value;
                    if (!editingCat) {
                      const slug = label.toLowerCase().replace(/\s+/g, '-').replace(/[^a-z0-9-]/g, '');
                      const parentSlug = form.parent_id
                        ? (categories.find(c => c.id === form.parent_id)?.id ?? form.parent_id)
                        : '';
                      const id = parentSlug ? `${parentSlug}-${slug}` : slug;
                      setForm(f => ({ ...f, label, id }));
                    } else {
                      setForm(f => ({ ...f, label }));
                    }
                  }} />
              </div>
              <div className="field-group">
                <label className="form-label">ID / Slug</label>
                <input type="text" className="input-field bg-sage-50 text-sage-400" value={form.id} readOnly />
              </div>
            </div>
            <div className="field-group">
              <label className="form-label">Parent Category (optional — makes this a subcategory)</label>
              <select className="input-field" value={form.parent_id} onChange={e => setForm(f => ({ ...f, parent_id: e.target.value }))}>
                <option value="">— Top level —</option>
                {parents.filter(p => p.id !== editingCat?.id).map(p => (
                  <option key={p.id} value={p.id}>{p.label}</option>
                ))}
              </select>
            </div>
            <div className="flex flex-wrap gap-6">
              <label className="flex items-center gap-2 cursor-pointer">
                <input type="checkbox" checked={form.next_day_only} className="w-4 h-4 accent-amber-400"
                  onChange={e => setForm(f => ({ ...f, next_day_only: e.target.checked }))} />
                <span className="text-sm font-medium text-charcoal">⚡ Next-day only</span>
              </label>
              <label className="flex items-center gap-2 cursor-pointer">
                <input type="checkbox" checked={form.active} className="w-4 h-4 accent-blush-300"
                  onChange={e => setForm(f => ({ ...f, active: e.target.checked }))} />
                <span className="text-sm font-medium text-charcoal">Active</span>
              </label>
            </div>
            {error && <p className="text-red-500 text-xs font-bold">{error}</p>}
            <div className="flex gap-3">
              <button onClick={handleSave} disabled={saving} className="btn-primary flex-1">
                {saving ? 'Saving…' : editingCat ? 'Update' : 'Save Category'}
              </button>
              <button onClick={() => { setShowForm(false); setEditingCat(null); setError(''); }} className="btn-secondary flex-1">Cancel</button>
            </div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* List */}
      {loading ? (
        <div className="flex justify-center py-16">
          <div className="w-8 h-8 border-2 border-blush-200 border-t-blush-400 rounded-full animate-spin" />
        </div>
      ) : (
        <div className="space-y-2">
          {parents.length === 0 && (
            <div className="text-center py-12 text-sage-400 text-sm">No categories yet — create the first one above.</div>
          )}
          {parents.map((parent, pIdx) => {
            const kids    = childrenOf(parent.id);
            const isOpen  = expandedParents.has(parent.id);
            const total   = totalDesignsFor(parent);
            const isEmpty = total === 0;
            return (
              <div key={parent.id} className="rounded-2xl border border-sage-100 overflow-hidden bg-white shadow-soft">
                {/* Parent row */}
                <div
                  draggable
                  onDragStart={() => parentDrag.onDragStart(pIdx)}
                  onDragEnter={() => parentDrag.onDragEnter(pIdx)}
                  onDragEnd={parentDrag.onDragEnd}
                  onDragOver={e => e.preventDefault()}
                  className="flex items-center gap-3 px-4 py-3 bg-sage-50/60 select-none"
                >
                  <div className="cursor-grab active:cursor-grabbing text-sage-300 hover:text-sage-500 transition-colors p-1 shrink-0">
                    <SafeIcon name="GripVertical" className="text-lg" />
                  </div>
                  <button onClick={() => toggleExpanded(parent.id)} className="text-sage-400 hover:text-charcoal transition-colors shrink-0">
                    <SafeIcon name={isOpen ? 'ChevronDown' : 'ChevronRight'} className="text-sm" />
                  </button>
                  <div className="flex-1 flex items-center gap-2 flex-wrap min-w-0">
                    <span className="font-bold text-charcoal text-sm">{parent.label}</span>
                    <span className="font-mono text-[11px] text-sage-400">{parent.id}</span>
                    <StatusBadge active={parent.active} />
                    {parent.next_day_only && <span className="px-2 py-0.5 rounded-full text-[10px] font-bold uppercase bg-amber-100 text-amber-600">⚡ Next-day</span>}
                    {isEmpty ? <EmptyBadge /> : <span className="px-2 py-0.5 rounded-full text-[10px] font-bold uppercase bg-sage-100 text-sage-500">{total} design{total !== 1 ? 's' : ''}</span>}
                    {kids.length > 0 && <span className="px-2 py-0.5 rounded-full text-[10px] font-bold uppercase bg-blush-50 text-blush-400 border border-blush-100">{kids.length} sub</span>}
                  </div>
                  <div className="flex items-center gap-3 shrink-0 text-xs font-bold uppercase tracking-wider">
                    <button onClick={() => openNew(parent.id)} className="text-blush-400 hover:text-blush-500 transition-colors">+ Sub</button>
                    <button onClick={() => toggleActive(parent)} className={parent.active ? 'text-amber-400 hover:text-amber-500' : 'text-green-500 hover:text-green-600'}>
                      {parent.active ? 'Deactivate' : 'Activate'}
                    </button>
                    <button onClick={() => openEdit(parent)} className="text-sage-400 hover:text-charcoal transition-colors">Edit</button>
                    {confirmDelete === parent.id ? (
                      <span className="flex items-center gap-1.5">
                        <span className="text-red-400">Sure?</span>
                        <button onClick={() => handleDelete(parent.id)} disabled={deleting} className="text-red-500">{deleting ? '…' : 'Yes'}</button>
                        <button onClick={() => setConfirmDelete(null)} className="text-sage-400">No</button>
                      </span>
                    ) : (
                      <button onClick={() => setConfirmDelete(parent.id)} className="text-red-400 hover:text-red-500 transition-colors">Delete</button>
                    )}
                  </div>
                </div>

                {/* Children */}
                <AnimatePresence initial={false}>
                  {isOpen && (
                    <motion.div initial={{ height: 0, opacity: 0 }} animate={{ height: 'auto', opacity: 1 }} exit={{ height: 0, opacity: 0 }} transition={{ duration: 0.18 }} className="overflow-hidden">
                      {kids.length === 0 ? (
                        <div className="px-14 py-3 text-xs text-sage-400 italic">No subcategories yet — click "+ Sub" to add one.</div>
                      ) : (
                        <div className="divide-y divide-sage-50">
                          {kids.map((child, cIdx) => {
                            const childCount = designCounts[child.id] || 0;
                            return (
                              <div key={child.id} className="flex items-center gap-3 px-4 py-2.5 pl-10 bg-white hover:bg-sage-50/40 transition-colors">
                                <div className="flex flex-col gap-0 shrink-0">
                                  <button onClick={() => moveChild(parent.id, child.id, -1)} disabled={cIdx === 0} className="text-sage-300 hover:text-sage-500 disabled:opacity-20 transition-colors leading-none py-0.5">
                                    <SafeIcon name="ChevronUp" className="text-xs" />
                                  </button>
                                  <button onClick={() => moveChild(parent.id, child.id, 1)} disabled={cIdx === kids.length - 1} className="text-sage-300 hover:text-sage-500 disabled:opacity-20 transition-colors leading-none py-0.5">
                                    <SafeIcon name="ChevronDown" className="text-xs" />
                                  </button>
                                </div>
                                <div className="w-px h-5 bg-sage-200 shrink-0" />
                                <div className="flex-1 flex items-center gap-2 flex-wrap min-w-0">
                                  <span className="font-medium text-charcoal text-sm">{child.label}</span>
                                  <span className="font-mono text-[11px] text-sage-400">{child.id}</span>
                                  <StatusBadge active={child.active} />
                                  {child.next_day_only && <span className="px-2 py-0.5 rounded-full text-[10px] font-bold uppercase bg-amber-100 text-amber-600">⚡</span>}
                                  {childCount === 0 ? <EmptyBadge /> : <span className="px-2 py-0.5 rounded-full text-[10px] font-bold uppercase bg-sage-100 text-sage-500">{childCount} design{childCount !== 1 ? 's' : ''}</span>}
                                </div>
                                <div className="flex items-center gap-3 shrink-0 text-xs font-bold uppercase tracking-wider">
                                  <button onClick={() => toggleActive(child)} className={child.active ? 'text-amber-400 hover:text-amber-500' : 'text-green-500 hover:text-green-600'}>
                                    {child.active ? 'Deactivate' : 'Activate'}
                                  </button>
                                  <button onClick={() => openEdit(child)} className="text-sage-400 hover:text-charcoal transition-colors">Edit</button>
                                  {confirmDelete === child.id ? (
                                    <span className="flex items-center gap-1.5">
                                      <span className="text-red-400">Sure?</span>
                                      <button onClick={() => handleDelete(child.id)} disabled={deleting} className="text-red-500">{deleting ? '…' : 'Yes'}</button>
                                      <button onClick={() => setConfirmDelete(null)} className="text-sage-400">No</button>
                                    </span>
                                  ) : (
                                    <button onClick={() => setConfirmDelete(child.id)} className="text-red-400 hover:text-red-500 transition-colors">Delete</button>
                                  )}
                                </div>
                              </div>
                            );
                          })}
                        </div>
                      )}
                    </motion.div>
                  )}
                </AnimatePresence>
              </div>
            );
          })}
        </div>
      )}

      {/* Footer summary */}
      {!loading && categories.length > 0 && (
        <div className="flex justify-between text-xs text-sage-400 pt-2 px-1">
          <span>{parents.length} top-level · {categories.filter(c => c.parent_id).length} subcategories</span>
          <span>{categories.filter(c => (designCounts[c.id] || 0) === 0).length} empty (auto-hidden from customers)</span>
        </div>
      )}
    </div>
  );
}
